import {
  DEFAULT_CUSTOM_STATUS_COLOR,
  PINNED_REDACTION_STATUS_FILTERS,
  REDACTION_STATUS_DEFINITIONS,
  REDACTION_STATUS_TYPES,
  REDACTION_STATUS_TYPE_AUDIO_VIDEO,
  REDACTION_STATUS_TYPE_DOCUMENT,
  REDACTION_STATUS_TYPE_IMAGE,
  RedactionStatusFilter,
  RedactionStatusKey,
  RedactionStatusSettings,
  RedactionStatusType,
  getEnabledRedactionStatusDefinitions,
  getRedactionStatusDisplayLabel,
  getDefaultRedactionStatusSettings,
  isPinnedRedactionStatusFilter,
  isRedactionStatusFilter,
  isRedactionStatusLabel,
  isWellKnownRedactionStatus,
  resolveRedactionStatusKey,
  parseRedactionStatusTypes,
  normalizeRedactionStatusFilter,
  normalizeRedactionStatusLabel,
  normalizeRedactionStatusSettings,
} from '@common-modules/mediaDetails/redactionStatuses';

/** Matches a 3- or 6-digit hex color; mirrors the drawer's write-path guard. */
const HEX_COLOR_REGEX = /^#([0-9a-f]{3}){1,2}$/i;

/** Fallback order for legacy custom rows (no `displayOrder`): sort last (VE-24546 #5). */
const LEGACY_CUSTOM_STATUS_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER;

export interface RedactionStatusSdoData {
  readonly statusLabel?: string;
  readonly statusLabelShort?: string;
  readonly statusKey?: RedactionStatusKey;
  /** Multi-select media types (prototype). Prefer over legacy `statusType`. */
  readonly statusTypes?: readonly RedactionStatusType[];
  /** Legacy single media-type field; still read for old rows. Prefer `statusTypes`. */
  readonly statusType?: string;
  readonly isCustomStatus?: boolean;
  readonly enabled: boolean;
  readonly colorHex: string;
  readonly colorName: string;
  readonly lockEditing: boolean;
  readonly lockEditingAvailable: boolean;
  readonly required?: boolean;
  readonly displayOrder: number;
  /** Email of the user who created / last modified the row (VE-24546; stored, not yet shown). */
  readonly createdBy?: string;
  readonly modifiedBy?: string;
  /**
   * Soft delete (VE-24546 follow-up): a deleted custom status keeps its SDO
   * row so files still tagged with it can resolve its attributes (name,
   * color, lock policy); it is hidden from the statuses page and pickers.
   */
  readonly isDeleted?: boolean;
}

/**
 * A customer-defined redaction status (VE-24546): a CRUD-able org settings SDO
 * row with multi-select media types and a `displayOrder` for drag-to-reorder.
 */
export interface RedactionCustomStatus {
  readonly id: string;
  readonly statusKey: string;
  readonly statusLabel: string;
  readonly statusTypes: readonly RedactionStatusType[];
  readonly enabled: boolean;
  readonly lockEditing: boolean;
  readonly colorHex: string;
  readonly colorName?: string;
  readonly displayOrder: number;
  /** Email of the creator / last modifier (VE-24546; stored, not yet shown). */
  readonly createdBy?: string;
  readonly modifiedBy?: string;
  readonly createdDateTime?: string;
  readonly modifiedDateTime?: string;
  /** Soft delete: hidden from the statuses page and pickers, but kept for resolving legacy-tagged files. */
  readonly isDeleted?: boolean;
}

export interface RedactionStatusSdoRecord {
  readonly id: string;
  readonly createdDateTime?: string;
  readonly modifiedDateTime?: string;
  readonly data: Partial<RedactionStatusSdoData>;
}

export interface RedactionStatusesState {
  readonly isFetchingStatuses: boolean;
  /**
   * True once a fetch has **succeeded** at least once this session (VE-24546).
   * Deliberately not set by a failed fetch: consumers use this to decide
   * whether `customStatuses` can be trusted, and lock-edit policy fails closed
   * while it is false. See `isRedactionStatusLockEdits`.
   */
  readonly hasLoadedStatuses: boolean;
  /**
   * True once any successful fetch returned at least one status record. Guards
   * the default-seeding save from overwriting an org's real settings when a
   * later fetch comes back empty (VE-24546).
   */
  readonly hasPersistedStatusRecords: boolean;
  readonly isSavingStatuses: boolean;
  readonly redactionStatuses: RedactionStatusSettings;
  /** SDO id keyed by `statusKey` (well-known or custom). */
  readonly sdoIdsByStatus: Record<string, string>;
  /** Customer-defined statuses, sorted by `displayOrder` (VE-24546). */
  readonly customStatuses: readonly RedactionCustomStatus[];
  readonly isStatusDrawerOpen: boolean;
  /** Custom status being edited, or undefined when adding a new one. */
  readonly selectedCustomStatus?: RedactionCustomStatus;
}

export interface FetchRedactionStatusesResponse {
  readonly structuredDataObjects: {
    readonly count: number;
    readonly records: RedactionStatusSdoRecord[];
  };
}

export interface SaveRedactionStatusesRequestPayload {
  readonly redactionStatuses: RedactionStatusSettings;
  readonly changedStatusFilter?: RedactionStatusFilter;
  readonly silent?: boolean;
}

export interface SaveRedactionStatusesSuccessPayload {
  readonly records: RedactionStatusSdoRecord[];
  readonly silent?: boolean;
}

export interface SaveRedactionCustomStatusRequestPayload {
  readonly customStatus: RedactionCustomStatus;
  /**
   * True only for the Add/Edit drawer's own Save. The settings table writes the
   * same action for its row toggles, and closing the drawer on those discarded
   * whatever the user was typing (VE-24546).
   */
  readonly closeDrawer?: boolean;
}

export interface DeleteRedactionCustomStatusRequestPayload {
  readonly sdoId: string;
  /** Deleted status name, for the success toast (VE-24546). */
  readonly statusLabel: string;
}

export interface ReorderRedactionCustomStatusesRequestPayload {
  readonly customStatuses: readonly RedactionCustomStatus[];
  /** Built-in settings to persist when a reorder also moved a built-in (VE-24546 #5); omitted otherwise. */
  readonly builtInSettings?: RedactionStatusSettings;
  /** Pre-reorder (server) snapshot for the thunk's diff/rollback, before the optimistic reducer overwrites the store (VE-24546 #1). */
  readonly previousCustomStatuses?: readonly RedactionCustomStatus[];
  readonly previousBuiltInSettings?: RedactionStatusSettings;
  /** Injected by the saga: SDO ids keyed by statusKey, for built-in upserts. */
  readonly sdoIdsByStatus?: Record<string, string>;
}

export interface SetEditCustomStatusPayload {
  readonly customStatus: RedactionCustomStatus;
}

export const getRedactionStatusSdoData = (
  settings: Partial<RedactionStatusSettings>
): RedactionStatusSdoData[] => {
  const normalizedSettings = normalizeRedactionStatusSettings(settings);

  return REDACTION_STATUS_DEFINITIONS.map((definition, index) => ({
    statusLabel: definition.label,
    statusLabelShort: definition.labelShort || definition.label,
    statusKey: definition.filter,
    // Built-ins apply to every media type (VE-24546).
    statusTypes: [...REDACTION_STATUS_TYPES],
    isCustomStatus: false,
    enabled: normalizedSettings[definition.filter].enabled,
    colorHex: definition.colorHex,
    colorName: definition.colorName,
    lockEditing: normalizedSettings[definition.filter].lockEditing,
    lockEditingAvailable: definition.lockEditingAvailable,
    required: definition.required,
    // Lets built-ins interleave with customs on the Statuses page (VE-24546 #5).
    displayOrder: normalizedSettings[definition.filter].displayOrder ?? index,
  }));
};

const getRecordStatusFilter = (
  data: Partial<RedactionStatusSdoData>
): RedactionStatusFilter | undefined => {
  if (isRedactionStatusFilter(data.statusKey)) {
    return data.statusKey;
  }
  if (isRedactionStatusLabel(data.statusLabel)) {
    return normalizeRedactionStatusFilter(data.statusLabel);
  }
  return undefined;
};

/**
 * Filter for a *built-in* row. Custom rows are excluded outright: the drawer
 * blocks a custom name that collides with a built-in label, but a row created
 * straight through the API can still carry one, and matching it by label would
 * let it hijack that built-in's enabled / lockEditing settings (VE-24546).
 */
const getBuiltInRecordStatusFilter = (
  data: Partial<RedactionStatusSdoData>
): RedactionStatusFilter | undefined =>
  data.isCustomStatus === true ? undefined : getRecordStatusFilter(data);

/** Resolves canonical `statusKey` for an org settings SDO row (well-known or custom). */
export const getRecordStatusKey = (
  data: Partial<RedactionStatusSdoData>
): string | undefined => {
  if (data.isCustomStatus && typeof data.statusKey === 'string') {
    const trimmed = data.statusKey.trim();
    return trimmed || undefined;
  }
  return getRecordStatusFilter(data);
};

export const getRedactionStatusSettingsFromRecords = (
  records: readonly RedactionStatusSdoRecord[]
): RedactionStatusSettings => {
  if (!records.length) {
    return getDefaultRedactionStatusSettings();
  }

  const settings = getLatestStatusRecords(records).reduce<
    Partial<RedactionStatusSettings>
  >((acc, record) => {
    const filter = getBuiltInRecordStatusFilter(record.data);
    if (!filter || !isRedactionStatusFilter(filter)) {
      return acc;
    }

    acc[filter] = {
      enabled: !!record.data.enabled,
      lockEditing: !!record.data.lockEditing,
      ...(typeof record.data.displayOrder === 'number'
        ? { displayOrder: record.data.displayOrder }
        : {}),
    };
    return acc;
  }, {});

  return normalizeRedactionStatusSettings(settings);
};

export const getPartialRedactionStatusSettingsFromRecords = (
  records: readonly RedactionStatusSdoRecord[]
): Partial<RedactionStatusSettings> =>
  getLatestStatusRecords(records).reduce<Partial<RedactionStatusSettings>>(
    (acc, record) => {
      const filter = getBuiltInRecordStatusFilter(record.data);
      if (!filter || !isRedactionStatusFilter(filter)) {
        return acc;
      }

      acc[filter] = {
        enabled: !!record.data.enabled,
        lockEditing: !!record.data.lockEditing,
        ...(typeof record.data.displayOrder === 'number'
          ? { displayOrder: record.data.displayOrder }
          : {}),
      };
      return acc;
    },
    {}
  );

export const getRedactionStatusSdoIdsFromRecords = (
  records: readonly RedactionStatusSdoRecord[]
): Record<string, string> =>
  getLatestStatusRecords(records).reduce<Record<string, string>>(
    (acc, record) => {
      const key = getRecordStatusKey(record.data);
      if (key) {
        acc[key] = record.id;
      }
      return acc;
    },
    {}
  );

const getRecordTimestamp = (record: RedactionStatusSdoRecord): number =>
  Date.parse(record.modifiedDateTime || record.createdDateTime || '') || 0;

const getLatestStatusRecords = (
  records: readonly RedactionStatusSdoRecord[]
): RedactionStatusSdoRecord[] => {
  const latestByKey: Record<string, RedactionStatusSdoRecord> = {};

  for (const record of records) {
    const key = getRecordStatusKey(record.data);
    if (!key) {
      continue;
    }
    const current = latestByKey[key];
    if (!current || getRecordTimestamp(record) >= getRecordTimestamp(current)) {
      latestByKey[key] = record;
    }
  }

  return Object.values(latestByKey);
};

const toRedactionCustomStatus = (
  record: RedactionStatusSdoRecord
): RedactionCustomStatus | undefined => {
  const { data } = record;
  const statusKey =
    typeof data.statusKey === 'string' ? data.statusKey.trim() : '';
  const statusLabel =
    typeof data.statusLabel === 'string' ? data.statusLabel.trim() : '';
  if (!statusKey || !statusLabel) {
    return undefined;
  }

  const statusTypes = parseRedactionStatusTypes(
    data.statusTypes ?? data.statusType
  );

  return {
    id: record.id,
    statusKey,
    statusLabel,
    // Rows written before v1.2 carry no statusTypes. Defaulting them to image
    // only would make them vanish from the document and A/V pickers on deploy;
    // all three types preserves where they already appear (VE-24546).
    statusTypes: statusTypes.length ? statusTypes : [...REDACTION_STATUS_TYPES],
    enabled: !!data.enabled,
    lockEditing: !!data.lockEditing,
    // Validate on read too: a non-hex string flows into a CSS color sink (VE-24546).
    colorHex:
      typeof data.colorHex === 'string' &&
      HEX_COLOR_REGEX.test(data.colorHex.trim())
        ? data.colorHex.trim()
        : DEFAULT_CUSTOM_STATUS_COLOR,
    colorName: typeof data.colorName === 'string' ? data.colorName : undefined,
    displayOrder:
      typeof data.displayOrder === 'number'
        ? data.displayOrder
        : LEGACY_CUSTOM_STATUS_DISPLAY_ORDER,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : undefined,
    modifiedBy:
      typeof data.modifiedBy === 'string' ? data.modifiedBy : undefined,
    createdDateTime: record.createdDateTime,
    modifiedDateTime: record.modifiedDateTime,
    isDeleted: data.isDeleted === true,
  };
};

/**
 * Extracts customer-defined statuses from org settings SDO records, keeping the
 * latest row per `statusKey` and sorting by `displayOrder` (then creation time).
 */
export const getRedactionCustomStatusesFromRecords = (
  records: readonly RedactionStatusSdoRecord[]
): RedactionCustomStatus[] =>
  getLatestStatusRecords(records)
    .filter((record) => record.data.isCustomStatus === true)
    .map(toRedactionCustomStatus)
    .filter((status): status is RedactionCustomStatus => status !== undefined)
    .sort((a, b) =>
      a.displayOrder !== b.displayOrder
        ? a.displayOrder - b.displayOrder
        : (Date.parse(a.createdDateTime || '') || 0) -
          (Date.parse(b.createdDateTime || '') || 0)
    );

/** Maps a `RedactionCustomStatus` to the SDO `data` payload persisted on save. */
export const getRedactionCustomStatusSdoData = (
  status: RedactionCustomStatus
): RedactionStatusSdoData => ({
  statusKey: status.statusKey,
  statusLabel: status.statusLabel,
  statusTypes: status.statusTypes,
  isCustomStatus: true,
  enabled: status.enabled,
  colorHex: status.colorHex,
  colorName: status.colorName || '',
  lockEditing: status.lockEditing,
  lockEditingAvailable: true,
  required: false,
  displayOrder: status.displayOrder,
  isDeleted: status.isDeleted === true,
  // Omit when unknown so we never write empty audit fields (VE-24546).
  ...(status.createdBy ? { createdBy: status.createdBy } : {}),
  ...(status.modifiedBy ? { modifiedBy: status.modifiedBy } : {}),
});

/** Next `displayOrder` for a new custom status: one past the largest existing order (VE-24546 #5). */
export const getNextCustomStatusDisplayOrder = (params: {
  readonly settings: Partial<RedactionStatusSettings> | undefined;
  readonly customStatuses: readonly RedactionCustomStatus[];
}): number => {
  const settings = normalizeRedactionStatusSettings(params.settings);
  const maxOrder = [
    ...REDACTION_STATUS_DEFINITIONS.map(
      (definition, index) => settings[definition.filter].displayOrder ?? index
    ),
    ...params.customStatuses.map((status) => status.displayOrder),
  ]
    // Skip the legacy sentinel; MAX_SAFE_INTEGER + 1 rounds and collides (VE-24546 #5).
    .filter((order) => order < LEGACY_CUSTOM_STATUS_DISPLAY_ORDER)
    .reduce(
      (max, order) => Math.max(max, order),
      REDACTION_STATUS_POOL_START - 1
    );
  return maxOrder + 1;
};

/** First order value available to the reorderable pool (after the pinned two). */
export const REDACTION_STATUS_POOL_START =
  PINNED_REDACTION_STATUS_FILTERS.length;

const BUILT_IN_ROW_PREFIX = 'builtin:';
const CUSTOM_ROW_PREFIX = 'custom:';

/** Stable row id for the Statuses page ordering (built-in filter or custom id). */
export const getBuiltInStatusRowId = (filter: RedactionStatusFilter): string =>
  `${BUILT_IN_ROW_PREFIX}${filter}`;
export const getCustomStatusRowId = (id: string): string =>
  `${CUSTOM_ROW_PREFIX}${id}`;

/**
 * A single row on the Statuses page (VE-24546 #5). Built-in and custom statuses
 * share one order below the pinned To Do / Draft rows.
 */
export interface OrderedRedactionStatusRow {
  readonly rowId: string;
  readonly displayOrder: number;
  readonly pinned: boolean;
  readonly builtInFilter?: RedactionStatusFilter;
  readonly custom?: RedactionCustomStatus;
}

/**
 * One ordered list: pinned To Do / Draft first, then the rest by shared
 * `displayOrder` (ties: built-ins before customs, then row id) (VE-24546 #5).
 */
export const getOrderedRedactionStatusRows = (params: {
  readonly settings: Partial<RedactionStatusSettings> | undefined;
  readonly customStatuses: readonly RedactionCustomStatus[];
}): OrderedRedactionStatusRow[] => {
  const settings = normalizeRedactionStatusSettings(params.settings);

  const builtInRows: OrderedRedactionStatusRow[] =
    REDACTION_STATUS_DEFINITIONS.map((definition, index) => ({
      rowId: getBuiltInStatusRowId(definition.filter),
      displayOrder: settings[definition.filter].displayOrder ?? index,
      pinned: isPinnedRedactionStatusFilter(definition.filter),
      builtInFilter: definition.filter,
    }));

  // Soft-deleted rows never appear on the statuses page.
  const customRows: OrderedRedactionStatusRow[] = params.customStatuses
    .filter((status) => !status.isDeleted)
    .map((status) => ({
      rowId: getCustomStatusRowId(status.id),
      displayOrder: status.displayOrder,
      pinned: false,
      custom: status,
    }));

  const pinnedOrder = (row: OrderedRedactionStatusRow): number =>
    (PINNED_REDACTION_STATUS_FILTERS as readonly string[]).indexOf(
      row.builtInFilter ?? ''
    );

  const pinned = builtInRows
    .filter((row) => row.pinned)
    .sort((a, b) => pinnedOrder(a) - pinnedOrder(b));

  const pool = [
    ...builtInRows.filter((row) => !row.pinned),
    ...customRows,
  ].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    if (!!a.custom !== !!b.custom) {
      return a.custom ? 1 : -1;
    }
    return a.rowId.localeCompare(b.rowId);
  });

  return [...pinned, ...pool];
};

/** Smallest gap between neighbours before midpoint insertion rebalances instead. */
const MIN_DISPLAY_ORDER_GAP = 1e-6;

interface ReindexedRedactionStatusPool {
  readonly settings: RedactionStatusSettings;
  readonly customStatuses: RedactionCustomStatus[];
  readonly builtInsChanged: boolean;
  readonly customsChanged: boolean;
}

/**
 * Reassigns `displayOrder` from the new pool order (pinned keep 0 / 1) and flags
 * whether built-in or custom orders changed, so callers skip no-op writes.
 *
 * `movedRowId` gets a midpoint order between its new neighbours so a drag only
 * rewrites that one row — every other row keeps its order (and therefore its
 * server `modifiedDateTime`). Falls back to a contiguous rebalance of the whole
 * pool when no row is named or the neighbour gap is exhausted (VE-24546 #5).
 */
export const reindexRedactionStatusPool = (params: {
  readonly orderedPoolRowIds: readonly string[];
  readonly settings: Partial<RedactionStatusSettings> | undefined;
  readonly customStatuses: readonly RedactionCustomStatus[];
  readonly movedRowId?: string;
}): ReindexedRedactionStatusPool => {
  const current = normalizeRedactionStatusSettings(params.settings);
  const nextSettings = {} as RedactionStatusSettings;
  for (const definition of REDACTION_STATUS_DEFINITIONS) {
    nextSettings[definition.filter] = { ...current[definition.filter] };
  }
  const nextCustoms = params.customStatuses.map((status) => ({ ...status }));
  const customById = new Map(nextCustoms.map((status) => [status.id, status]));

  const builtInFilterOf = (
    rowId: string
  ): RedactionStatusFilter | undefined => {
    if (!rowId.startsWith(BUILT_IN_ROW_PREFIX)) {
      return undefined;
    }
    const filter = rowId.slice(BUILT_IN_ROW_PREFIX.length);
    return isRedactionStatusFilter(filter) &&
      !isPinnedRedactionStatusFilter(filter)
      ? filter
      : undefined;
  };

  const setOrder = (rowId: string, order: number): void => {
    const filter = builtInFilterOf(rowId);
    if (filter) {
      nextSettings[filter].displayOrder = order;
      return;
    }
    const custom = customById.get(rowId.slice(CUSTOM_ROW_PREFIX.length));
    if (custom) {
      custom.displayOrder = order;
    }
  };

  const orderOf = (rowId: string): number | undefined => {
    const filter = builtInFilterOf(rowId);
    if (filter) {
      return current[filter].displayOrder;
    }
    return customById.get(rowId.slice(CUSTOM_ROW_PREFIX.length))?.displayOrder;
  };

  const finish = (): ReindexedRedactionStatusPool => ({
    settings: nextSettings,
    customStatuses: nextCustoms,
    builtInsChanged: REDACTION_STATUS_DEFINITIONS.some(
      (definition) =>
        current[definition.filter].displayOrder !==
        nextSettings[definition.filter].displayOrder
    ),
    customsChanged: nextCustoms.some((status, index) => {
      const previous = params.customStatuses[index];
      return !previous || previous.displayOrder !== status.displayOrder;
    }),
  });

  const rebalance = (): ReindexedRedactionStatusPool => {
    params.orderedPoolRowIds.forEach((rowId, index) => {
      setOrder(rowId, REDACTION_STATUS_POOL_START + index);
    });
    return finish();
  };

  const movedIndex = params.movedRowId
    ? params.orderedPoolRowIds.indexOf(params.movedRowId)
    : -1;
  if (!params.movedRowId || movedIndex < 0) {
    return rebalance();
  }

  // Neighbours in the new order; a legacy sentinel order can't be bisected.
  const previousId = params.orderedPoolRowIds[movedIndex - 1];
  const nextId = params.orderedPoolRowIds[movedIndex + 1];
  const lowerBound = previousId
    ? orderOf(previousId)
    : REDACTION_STATUS_POOL_START - 1;
  const upperBound = nextId ? orderOf(nextId) : undefined;
  const boundsUnusable =
    lowerBound === undefined ||
    lowerBound >= LEGACY_CUSTOM_STATUS_DISPLAY_ORDER ||
    (nextId !== undefined &&
      (upperBound === undefined ||
        upperBound >= LEGACY_CUSTOM_STATUS_DISPLAY_ORDER));
  if (boundsUnusable) {
    return rebalance();
  }

  const movedOrder =
    upperBound === undefined ? lowerBound + 1 : (lowerBound + upperBound) / 2;
  // Exhausted float precision between neighbours — renumber the pool instead.
  if (
    upperBound !== undefined &&
    (movedOrder - lowerBound < MIN_DISPLAY_ORDER_GAP ||
      upperBound - movedOrder < MIN_DISPLAY_ORDER_GAP)
  ) {
    return rebalance();
  }
  setOrder(params.movedRowId, movedOrder);
  return finish();
};

/**
 * A row in the file/editor status picker: built-in (i18n messageId) or custom
 * (plain `displayLabel`). `value` is written to the TDO `redactionStatus` tag —
 * built-in labels for product statuses, `statusKey` for custom (stable identity).
 */
export interface SelectableRedactionStatus {
  readonly id: string;
  readonly value: string;
  readonly displayLabel: string;
  readonly messageId?: string;
  readonly colorHex: string;
  readonly lockEditing: boolean;
  readonly isCustom: boolean;
}

/**
 * Whether a status-picker option is the file's current status — drives the
 * checkmark/selected row in every status dropdown (VE-24546).
 *
 * The primary match is by canonical key. The label clause exists only for
 * legacy tags that stored a custom status by label rather than key, and is
 * deliberately gated on the current value NOT resolving to a built-in:
 * built-ins win label collisions everywhere else (`findCustomRedactionStatus`),
 * so when a legacy custom shares a built-in label and the tag stores that
 * label, only the built-in row may be current — without the gate both rows
 * would show a checkmark. Label comparison is trim+lowercase, matching the
 * other status matchers.
 */
export const isCurrentRedactionStatusOption = (
  option: Pick<
    SelectableRedactionStatus,
    'value' | 'displayLabel' | 'isCustom'
  >,
  currentStatus: string | undefined
): boolean => {
  if (typeof currentStatus !== 'string') {
    return false;
  }
  const currentKey = resolveRedactionStatusKey(currentStatus);
  if (!currentKey) {
    return false;
  }
  if (resolveRedactionStatusKey(option.value) === currentKey) {
    return true;
  }
  return (
    option.isCustom &&
    !isWellKnownRedactionStatus(currentStatus) &&
    option.displayLabel.trim().toLowerCase() ===
      currentStatus.trim().toLowerCase()
  );
};

/**
 * True when `name` collides with a built-in status label or another custom
 * status label (case-insensitive). Used by the Add/Edit Status drawer.
 */
export const isRedactionStatusNameTaken = (params: {
  readonly name: string;
  readonly customStatuses: readonly RedactionCustomStatus[];
  /** When editing, exclude this row from the duplicate check. */
  readonly excludeStatusKey?: string;
}): boolean => {
  const normalized = params.name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  if (
    REDACTION_STATUS_DEFINITIONS.some(
      (definition) => definition.label.toLowerCase() === normalized
    )
  ) {
    return true;
  }
  // Soft-deleted rows free their name: they are invisible on the statuses
  // page, so blocking the name would be inexplicable to the user. Tag
  // resolution prefers live rows on a label collision
  // (findCustomRedactionStatus).
  return params.customStatuses.some(
    (status) =>
      !status.isDeleted &&
      status.statusKey !== params.excludeStatusKey &&
      status.statusLabel.trim().toLowerCase() === normalized
  );
};

/**
 * Maps a content-type / MIME string to the Statuses media-type multi-select
 * keys used to filter which custom statuses appear for a file.
 */
export const getRedactionStatusTypeFromContentType = (
  contentType: string | undefined | null
): RedactionStatusType | undefined => {
  if (!contentType) {
    return undefined;
  }
  const ct = contentType.toLowerCase();
  if (ct.includes('image')) {
    return REDACTION_STATUS_TYPE_IMAGE;
  }
  if (
    ct.includes('pdf') ||
    // cspell:ignore msword opendocument
    ct.includes('msword') ||
    ct.includes('officedocument') ||
    ct.includes('opendocument') ||
    ct.includes('text/') ||
    ct.includes('application/rtf') ||
    ct.includes('message/rfc822') ||
    // Outlook MSG — same document MIME allowlist as helpers/constants.ts
    ct.includes('application/vnd.ms-outlook')
  ) {
    return REDACTION_STATUS_TYPE_DOCUMENT;
  }
  if (ct.includes('audio') || ct.includes('video')) {
    return REDACTION_STATUS_TYPE_AUDIO_VIDEO;
  }
  return undefined;
};

/**
 * Built-in enabled statuses plus enabled custom statuses (optionally filtered
 * by media type). `mediaType` = single file; `mediaTypes` = bulk selection
 * intersection (custom must include every type in the set).
 */
export const getSelectableRedactionStatuses = (params: {
  readonly settings: Partial<RedactionStatusSettings> | undefined;
  readonly customStatuses: readonly RedactionCustomStatus[];
  readonly mediaType?: RedactionStatusType;
  readonly mediaTypes?: readonly RedactionStatusType[];
}): SelectableRedactionStatus[] => {
  const enabledByFilter = new Map(
    getEnabledRedactionStatusDefinitions(params.settings).map((definition) => [
      definition.filter,
      definition,
    ])
  );

  const requiredTypes =
    params.mediaTypes && params.mediaTypes.length > 0
      ? params.mediaTypes
      : params.mediaType
        ? [params.mediaType]
        : undefined;

  // Same order as the Statuses settings page: pinned To Do / Draft first,
  // then the pool interleaved by displayOrder (VE-24546).
  return getOrderedRedactionStatusRows({
    settings: params.settings,
    customStatuses: params.customStatuses,
  }).flatMap((row): SelectableRedactionStatus[] => {
    if (row.builtInFilter) {
      const definition = enabledByFilter.get(row.builtInFilter);
      if (!definition) {
        return [];
      }
      return [
        {
          id: definition.filter,
          value: definition.label,
          displayLabel: definition.label,
          messageId: definition.messageId,
          colorHex: definition.colorHex,
          lockEditing: definition.lockEditing,
          isCustom: false,
        },
      ];
    }
    const status = row.custom;
    if (
      !status?.enabled ||
      status.isDeleted ||
      (requiredTypes &&
        !requiredTypes.every((type) => status.statusTypes.includes(type)))
    ) {
      return [];
    }
    return [
      {
        id: status.statusKey,
        // Persist statusKey on the TDO tag so rename / built-in label collisions
        // cannot steal the file's status identity (VE-24546).
        value: status.statusKey,
        displayLabel: status.statusLabel,
        colorHex: status.colorHex,
        lockEditing: status.lockEditing,
        isCustom: true,
      },
    ];
  });
};

export const normalizeRedactionStatusSdoRecord = (
  record: RedactionStatusSdoRecord
): RedactionStatusSdoRecord => {
  if (record.data.isCustomStatus && typeof record.data.statusKey === 'string') {
    const trimmedKey = record.data.statusKey.trim();
    return {
      ...record,
      data: {
        ...record.data,
        statusKey: trimmedKey,
      },
    };
  }

  const normalizedStatusKey = normalizeRedactionStatusFilter(
    record.data.statusKey || record.data.statusLabel
  );

  return {
    ...record,
    data: {
      ...record.data,
      statusKey: normalizedStatusKey,
      statusLabel: getRedactionStatusDisplayLabel({
        statusKey: normalizedStatusKey,
        statusLabel: normalizeRedactionStatusLabel(record.data.statusLabel),
        isCustomStatus: false,
      }),
    },
  };
};
